/** The tracker's columns: which of the workbook's 29 headers the screen can
 * show, the short label it shows them under, and which a viewer has chosen.
 *
 * The server sends the workbook's own header text ("Completion Date -
 * Estimated", "Statiion Type" with its misspelling) because the Excel export
 * must match the client's spreadsheet to the letter. The screen is read by
 * people, not by a Power Query step, so it shows a short label and keeps the
 * workbook's header as the column's tooltip: whoever reconciles the two can
 * still see which is which. */

export type ColumnGroup = "Job" | "Documents" | "Money";

export interface ColumnDef {
  /** The workbook header, exactly as the server sends it. */
  key: string;
  label: string;
  group: ColumnGroup;
  kind: "text" | "date" | "money" | "status";
}

/** The two headers the sticky SITE column is made of: the station's name
 * over its works order. They are always shown, so they are not offered. */
export const SITE_NAME = "Station Name";
export const WORKS_ORDER = "QCP Number";

export const STATUS = "Status";
export const EXCLUDING_VAT = "Excluding Vat";
export const SUPPLIER_COST = "Service Provider Inv Amount Excl Vat";
export const GROSS_PROFIT = "Gross Profit";
export const INVOICE_NO = "Inv No";

/** The site statuses, in workflow order, with the words the rest of the app
 * uses for them (`prv.web.api.sites.STATUS_LABELS`). The tracker's Status
 * column and its filter pills are the site's own status -- there is no
 * separate job status behind the workbook. */
export const SITE_STATUSES: readonly { value: string; label: string }[] = [
  { value: "assessment_pending", label: "Assessment pending" },
  { value: "assessment_in_progress", label: "Assessment in progress" },
  { value: "assessment_completed", label: "Assessment completed" },
  { value: "service_overhaul", label: "Service / overhaul" },
  { value: "service_completed", label: "Service completed" },
  { value: "replacement", label: "Replacement" },
  { value: "replacement_completed", label: "Replacement completed" },
  { value: "completed", label: "Completed" },
];

export function statusLabel(value: string): string {
  return SITE_STATUSES.find((status) => status.value === value)?.label ?? value.replace(/_/g, " ");
}

/** In the order the screen shows them, grouped as the picker lists them. */
export const COLUMN_DEFS: readonly ColumnDef[] = [
  { key: STATUS, label: "Status", group: "Job", kind: "status" },
  { key: "Service Provider", label: "Provider", group: "Job", kind: "text" },
  { key: "Region", label: "Region", group: "Job", kind: "text" },
  { key: "Cluster", label: "Cluster", group: "Job", kind: "text" },
  { key: "Site", label: "Facility", group: "Job", kind: "text" },
  { key: "Statiion Type", label: "Station type", group: "Job", kind: "text" },
  { key: "Location/Street", label: "Street", group: "Job", kind: "text" },
  { key: "Scope", label: "Scope", group: "Job", kind: "text" },
  { key: "QCP Date", label: "QCP date", group: "Job", kind: "date" },
  { key: "Completion Date - Estimated", label: "Planned", group: "Job", kind: "date" },
  { key: "Completion Date - Actual", label: "Completed", group: "Job", kind: "date" },
  { key: "Completion Month - Actual", label: "Completed month", group: "Job", kind: "text" },
  { key: "BOQ Date", label: "BOQ date", group: "Documents", kind: "date" },
  { key: "BOQ Number", label: "BOQ", group: "Documents", kind: "text" },
  { key: "Order Date", label: "Order date", group: "Documents", kind: "date" },
  { key: "Order Number", label: "Customer PO", group: "Documents", kind: "text" },
  { key: "Order Number Status", label: "Order status", group: "Documents", kind: "text" },
  { key: "Service Provider PO Number", label: "Supplier PO", group: "Documents", kind: "text" },
  { key: "Service Provider Inv Date", label: "Supplier inv date", group: "Documents", kind: "date" },
  { key: "Service Provider Inv No", label: "Supplier inv no", group: "Documents", kind: "text" },
  { key: "Inv Date", label: "Inv date", group: "Documents", kind: "date" },
  { key: INVOICE_NO, label: "Inv no", group: "Documents", kind: "text" },
  { key: SUPPLIER_COST, label: "Supplier cost", group: "Money", kind: "money" },
  { key: EXCLUDING_VAT, label: "Excluding VAT", group: "Money", kind: "money" },
  { key: "Including Vat", label: "Including VAT", group: "Money", kind: "money" },
  { key: "Inv Amount", label: "Inv amount", group: "Money", kind: "money" },
  { key: GROSS_PROFIT, label: "Gross profit", group: "Money", kind: "money" },
];

export const GROUPS: readonly ColumnGroup[] = ["Job", "Documents", "Money"];

/** What a first visit shows: where the job is and what it earned. */
export const DEFAULT_VISIBLE: readonly string[] = [
  STATUS,
  "Service Provider",
  "Completion Date - Estimated",
  "Completion Date - Actual",
  "BOQ Number",
  "Order Number",
  INVOICE_NO,
  EXCLUDING_VAT,
  GROSS_PROFIT,
];

/** The columns this report can show: the definitions the server actually
 * sent. A crew's report has no money headers, so its picker has none. */
export function availableColumns(serverColumns: readonly string[]): ColumnDef[] {
  const sent = new Set(serverColumns);
  return COLUMN_DEFS.filter((column) => sent.has(column.key));
}

export interface ColumnStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const COLUMNS_KEY = "prv.tracker.columns";

/** The browser's storage, or none: a private window or blocked site data
 * throws on the mere access, and the picker must still work for the visit. */
export function browserStore(): ColumnStore | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** The viewer's choice, or the default when there is none or it is rubbish.
 * An empty list is a choice (only the site column), not a missing one. */
export function readVisible(store: ColumnStore | null): string[] {
  if (store === null) {
    return [...DEFAULT_VISIBLE];
  }
  try {
    const raw = store.getItem(COLUMNS_KEY);
    if (raw === null) {
      return [...DEFAULT_VISIBLE];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.every((item) => typeof item === "string")) {
      return [...DEFAULT_VISIBLE];
    }
    const known = new Set(COLUMN_DEFS.map((column) => column.key));
    return parsed.filter((key) => known.has(key));
  } catch {
    return [...DEFAULT_VISIBLE];
  }
}

/** Remember the choice. A store that refuses (full, blocked) costs only the
 * memory of it; the screen keeps the columns for this visit. */
export function writeVisible(store: ColumnStore | null, visible: readonly string[]): void {
  if (store === null) {
    return;
  }
  try {
    store.setItem(COLUMNS_KEY, JSON.stringify(visible));
  } catch {
    // Nothing to do: the choice lasts until the page is left.
  }
}

/** `visible` with `key` switched, as a new list. */
export function toggled(visible: readonly string[], key: string): string[] {
  return visible.includes(key) ? visible.filter((item) => item !== key) : [...visible, key];
}
